import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import type { LegendItemClickEventObject, Point, Series } from 'highcharts'
import { escape as escapeHtml } from 'lodash-es'
import { matchPath } from 'react-router-dom'

import { UPLOAD_DATE_OPTIONS } from 'consts/filter/uploadDate'
import { ALL_TIME_RANGE_QUERY } from 'consts/filterOptions'
import { SparklineIntervalTypeEnum, TimeUnit } from 'generated/graphql'
import type {
  RangeQuery,
  TimeRange,
  GetPortfolioTrackingVolumeQueryVariables,
  GetPortfolioTrackingViralityQueryVariables,
  GetPortfolioTrackingVelocityQueryVariables,
  GetPortfolioTrackingVarietyQueryVariables,
} from 'generated/graphql'
import type { TBrandComponentKey } from 'modules/winnin-auth-permissions/common/winnin-auth-permissions.types'
import {
  getUserComponentPermissions,
  getUserRules,
} from 'modules/winnin-auth-permissions/common/winnin-auth-permissions.utils'
import { UserStore } from 'stores'
import { getCustomTimeDifference } from 'utils/getCustomTimeDifference'
import { getCustomTimeUnitByDays } from 'utils/getCustomTimeUnitByDays'
import { convertTimeUnit } from 'utils/getCustomTimeUnitInterval'
import getUploadDateByValue from 'utils/getUploadDateByValue'
import { getFormatedDateVariablesForTerritory } from 'utils/territoryUtils'

import {
  BASE_X_AXIS_OPTIONS,
  BRAND_PAGE_PATHS,
  MEDIA_TYPES,
  PERIOD_LABEL_FORMAT,
  TIER_IDS,
  TIER_SUBSCRIBER_RANGES,
  TIME_UNIT_TO_DAYJS_UNIT,
  VARIETY_POOL_SUBSCRIBER_RANGE,
  WEEK_RANGE_SEPARATOR,
} from './winnin-lite.consts'
import type {
  IPortfolioTrackingBrand,
  IPortfolioTrackingBrandVariety,
  IPortfolioTrackingBrandVelocity,
  IPortfolioTrackingBrandVirality,
  IPortfolioTrackingBrandVolume,
  IPortfolioTrackingSparklinePoint,
  IPortfolioTrackingTooltipContent,
  IPortfolioTrackingTooltipRow,
  TBrandNode,
  TMediaType,
  TPeriodType,
  TPortfolioTrackingTierId,
  TPortfolioTrackingXAxisOptions,
  TTerritoryNode,
  TVarietyQuery,
  TVelocityQuery,
  TViralityQuery,
  TVolumeQuery,
} from './winnin-lite.types'

dayjs.extend(utc)

export const isBrandComponentHidden = (key: TBrandComponentKey): boolean =>
  getUserComponentPermissions(UserStore.getActiveWorkspaceModules)?.brandComponents?.[key]?.hidden === true

const isOnBrandPage = (): boolean => Boolean(matchPath(window.location.pathname, { path: BRAND_PAGE_PATHS }))

export const isBrandPageComponentHidden = (key: TBrandComponentKey): boolean =>
  isOnBrandPage() && isBrandComponentHidden(key)

export const isBrandComponentBlocked = (key: TBrandComponentKey): boolean =>
  getUserComponentPermissions(UserStore.getActiveWorkspaceModules)?.brandComponents?.[key]?.blocked === true

export const isBrandComponentModuleOnly = (key: TBrandComponentKey): boolean => {
  const permission = getUserComponentPermissions(UserStore.getActiveWorkspaceModules)?.brandComponents?.[key]
  return Boolean(permission) && permission?.hidden !== true
}

export const isUploadDateRestrictedToCurrentYear = (): boolean =>
  getUserRules(UserStore.getActiveWorkspaceModules)?.restrictUploadDateToCurrentYear === true

/** A Series owns the chart; a Point reaches it through its series. Neither test reads `points`, which
 * is absent on a Point and empty on a series Highcharts has not generated points for. */
const isLegendSeries = (item: LegendItemClickEventObject['legendItem']): item is Series => 'chart' in item
const isLegendPoint = (item: LegendItemClickEventObject['legendItem']): item is Point => 'series' in item

/**
 * Keeps at least one legend item visible, so a chart can never be emptied by hiding everything.
 *
 * `event.legendItem` is a Series for series-per-legend charts (column, line) and a Point for pies.
 * Highcharts also types it as LegendItemObject — a bag of SVG elements with no chart data on it —
 * which is the arm bubble legends use, so neither guard is guaranteed to match. When no siblings can
 * be resolved the click is blocked rather than allowed: an item we cannot reason about must not be
 * the one that empties the chart, and this must never throw out of a click handler.
 */
export function preventHidingLastLegendItem(event: LegendItemClickEventObject): void {
  const item = event.legendItem

  const target: Series | Point | undefined = isLegendSeries(item) || isLegendPoint(item) ? item : undefined
  const siblings = isLegendSeries(item) ? item.chart?.series : isLegendPoint(item) ? item.series?.points : undefined

  if (!target || !siblings?.length) {
    event.preventDefault()
    return
  }

  const visibleCount = siblings.filter(sibling => sibling.visible).length

  if (target.visible && visibleCount <= 1) event.preventDefault()
}

// Tooltips render with useHTML, so anything interpolated here is parsed as markup. Labels and
// values carry backend-provided brand names, so escape every interpolation.
const renderTooltipRow = ({ label, value, color, description }: IPortfolioTrackingTooltipRow): string => {
  const marker = color
    ? `<span class="PortfolioTrackingTooltip-marker" style="background:${escapeHtml(color)}"></span>`
    : ''
  const descriptionHtml = description
    ? `<span class="PortfolioTrackingTooltip-description">${escapeHtml(description)}</span>`
    : ''

  return `<div class="PortfolioTrackingTooltip-row">
    <div class="PortfolioTrackingTooltip-label">${marker}<span>${escapeHtml(label)}</span>${descriptionHtml}</div>
    <span class="PortfolioTrackingTooltip-value">${escapeHtml(value)}</span>
  </div>`
}

export const getPortfolioTrackingXAxisOptions = (
  periodType: TPeriodType,
  xAxisLabels: string[]
): TPortfolioTrackingXAxisOptions => {
  if (periodType !== 'week') return BASE_X_AXIS_OPTIONS

  return {
    ...BASE_X_AXIS_OPTIONS,
    labels: {
      formatter(this: { value: number | string }) {
        const label = xAxisLabels[Number(this.value)] ?? String(this.value)
        return label.split(WEEK_RANGE_SEPARATOR)[0]
      },
    },
  }
}

export const buildPortfolioTrackingTooltip = ({
  title,
  period,
  rows,
  total,
}: IPortfolioTrackingTooltipContent): string => {
  const periodHtml = period ? `<span class="PortfolioTrackingTooltip-period">${escapeHtml(period)}</span>` : ''

  const totalHtml = total
    ? `<div class="PortfolioTrackingTooltip-total">${renderTooltipRow({ label: total.label, value: total.value })}</div>`
    : ''

  return `<div class="PortfolioTrackingTooltip">
    <div class="PortfolioTrackingTooltip-header">
      <span class="PortfolioTrackingTooltip-title">${escapeHtml(title)}</span>
      ${periodHtml}
    </div>
    <div class="PortfolioTrackingTooltip-rows">${rows.map(renderTooltipRow).join('')}</div>
    ${totalHtml}
  </div>`
}

const resolveDateRange = (uploadDateParam: string): { timeRange: TimeRange | null; publishedAt: RangeQuery | null } => {
  const isCustomDate = uploadDateParam.includes('custom')
  const isAllTime = uploadDateParam === 'all'
  const isUsingTimeRange = !isCustomDate && !isAllTime

  if (isUsingTimeRange) {
    return { timeRange: getFormatedDateVariablesForTerritory(uploadDateParam), publishedAt: null }
  }

  return { timeRange: null, publishedAt: isCustomDate ? getUploadDateByValue(uploadDateParam) : ALL_TIME_RANGE_QUERY }
}

const toUtcCalendarDay = (timestamp: number): dayjs.Dayjs => dayjs.utc(dayjs(timestamp).format('YYYY-MM-DD'))

/**
 * The date picker writes custom bounds as browser-local midnights, but the API is queried on whole
 * UTC days: each bound is re-anchored on its local calendar date at UTC, and the last day is
 * included up to its final millisecond. Chart math keeps reading the local bounds from
 * `resolveDateRange`, since the velocity buckets are already rendered on their calendar dates.
 */
const resolveQueryDateRange = (
  uploadDateParam: string
): { timeRange: TimeRange | null; publishedAt: RangeQuery | null } => {
  const range = resolveDateRange(uploadDateParam)
  const gte = range.publishedAt?.gte
  const lte = range.publishedAt?.lte

  if (!uploadDateParam.includes('custom') || gte == null || lte == null) return range

  return {
    timeRange: null,
    publishedAt: { gte: toUtcCalendarDay(gte).valueOf(), lte: toUtcCalendarDay(lte).endOf('day').valueOf() },
  }
}

/**
 * metrics() takes timeRange and publishedAt as separate top-level arguments. Volume and Virality send
 * the same pair, so both cards read the same territory performance metrics call.
 */
export const buildVolumeVariables = (uploadDateParam: string): GetPortfolioTrackingVolumeQueryVariables =>
  resolveQueryDateRange(uploadDateParam)

export const buildViralityVariables = (uploadDateParam: string): GetPortfolioTrackingViralityQueryVariables =>
  resolveQueryDateRange(uploadDateParam)

const VELOCITY_MONTH_RANGE_WEEK_THRESHOLD = 3

const resolveVelocityPeriodTypeForTimeRange = (timeRange: TimeRange): TPeriodType => {
  if (timeRange.timeUnit !== TimeUnit.Month) return 'day'
  return timeRange.timeValue <= VELOCITY_MONTH_RANGE_WEEK_THRESHOLD ? 'week' : 'month'
}

export const resolveVelocityPeriodType = (uploadDateParam: string): TPeriodType => {
  const { timeRange } = resolveDateRange(uploadDateParam)
  if (timeRange) return resolveVelocityPeriodTypeForTimeRange(timeRange)
  return getCustomTimeUnitByDays(getCustomTimeDifference(uploadDateParam))
}

// Days the selected upload-date range actually spans. The velocity sparkline is bucketed by
// periodType (day / week / month), so a bucket count is not a day count — the per-day average has to
// divide by this instead. Presets are resolved from their own timeUnit/timeValue (so "last 3 months"
// counts the real calendar days, not 3 x 30), and custom/all-time from their explicit gte/lte.
export const resolveVelocityRangeDays = (uploadDateParam: string): number => {
  const { timeRange, publishedAt } = resolveDateRange(uploadDateParam)

  if (timeRange) {
    const unit = TIME_UNIT_TO_DAYJS_UNIT[timeRange.timeUnit]
    if (!unit) return 0

    const end = dayjs()
    return end.diff(end.subtract(timeRange.timeValue, unit), 'day')
  }

  if (!publishedAt?.gte) return 0

  const start = dayjs(publishedAt.gte).startOf('day')
  const end = dayjs(publishedAt.lte ?? undefined).endOf('day')
  return Math.max(end.diff(start, 'day') + 1, 0)
}

// First and last day the range covers. The velocity buckets need both to size a leading or trailing
// partial bucket, since a range can start and end mid-week/month.
export const resolveRangeStart = (uploadDateParam: string): number => {
  const { timeRange, publishedAt } = resolveDateRange(uploadDateParam)

  if (timeRange) {
    const unit = TIME_UNIT_TO_DAYJS_UNIT[timeRange.timeUnit]
    return (unit ? dayjs().subtract(timeRange.timeValue, unit) : dayjs()).startOf('day').valueOf()
  }

  return dayjs(publishedAt?.gte ?? undefined)
    .startOf('day')
    .valueOf()
}

export const resolveRangeEnd = (uploadDateParam: string): number => {
  const { publishedAt } = resolveDateRange(uploadDateParam)
  const today = dayjs().endOf('day')
  if (!publishedAt?.lte) return today.valueOf()

  const end = dayjs(publishedAt.lte).endOf('day')
  return (end.isAfter(today) ? today : end).valueOf()
}

export const buildVelocityVariables = (uploadDateParam: string): GetPortfolioTrackingVelocityQueryVariables => {
  const { timeRange, publishedAt } = resolveQueryDateRange(uploadDateParam)
  return {
    filters: {
      ...(timeRange ? { timeRange } : { publishedAt }),
      interval: {
        timeUnit: convertTimeUnit(resolveVelocityPeriodType(uploadDateParam)),
        type: SparklineIntervalTypeEnum.Calendar,
      },
    },
  }
}

export const getPortfolioTrackingPeriodLabel = (uploadDateParam: string): string => {
  if (uploadDateParam.includes('custom')) {
    const [, gte, lte] = uploadDateParam.split('_')
    const start = dayjs(Number(gte))
    const end = dayjs(Number(lte))
    return `${start.format(PERIOD_LABEL_FORMAT)} – ${end.format(PERIOD_LABEL_FORMAT)} ${end.format('YYYY')}`
  }

  const option = UPLOAD_DATE_OPTIONS.find(opt => String(opt.key) === uploadDateParam)
  if (!option?.start || !option?.end) return ''

  const start = dayjs(option.start)
  const end = dayjs(option.end)
  return `${start.format(PERIOD_LABEL_FORMAT)} – ${end.format(PERIOD_LABEL_FORMAT)} ${end.format('YYYY')}`
}

// `filters` bounds the creator pool the backend collects per territory; the mega…nano aliases slice
// that pool by tier. Don't add `filters.audience` here: the total-creators path rejects it with
// `400 total-creators-audience-unsupported` on purpose, because the upstream would accept the field
// and then silently ignore it. Audience filtering is incompatible with shouldReturnTotalCreators.
export const buildVarietyVariables = (uploadDateParam: string): GetPortfolioTrackingVarietyQueryVariables => {
  const { timeRange, publishedAt } = resolveQueryDateRange(uploadDateParam)
  return {
    filters: {
      ...(timeRange ? { timeRange } : { publishedAt }),
      subscribers: VARIETY_POOL_SUBSCRIBER_RANGE,
    },
    mega: { subscribers: TIER_SUBSCRIBER_RANGES.mega },
    macro: { subscribers: TIER_SUBSCRIBER_RANGES.macro },
    mid: { subscribers: TIER_SUBSCRIBER_RANGES.mid },
    micro: { subscribers: TIER_SUBSCRIBER_RANGES.micro },
    nano: { subscribers: TIER_SUBSCRIBER_RANGES.nano },
  }
}

const getBrandInfo = (brand: { id: string; name: string; thumbnail?: string | null }): IPortfolioTrackingBrand => ({
  id: brand.id,
  name: brand.name,
  thumbnail: brand.thumbnail ?? null,
})

const collectBrandNodes = (
  workspaceBrand: TBrandNode | null | undefined,
  competitors: (TBrandNode | null)[] | null | undefined
): TBrandNode[] => {
  const nodes: TBrandNode[] = []
  if (workspaceBrand) nodes.push(workspaceBrand)
  ;(competitors ?? []).forEach(competitor => {
    if (competitor) nodes.push(competitor)
  })
  return nodes
}

function buildByMedia<TValue>(
  territories: Record<TMediaType, TTerritoryNode | null>,
  read: (territory: TTerritoryNode) => TValue,
  emptyValue: TValue
): Record<TMediaType, TValue> {
  return MEDIA_TYPES.reduce(
    (acc, media) => {
      const territory = territories?.[media]
      acc[media] = territory ? read(territory) : emptyValue
      return acc
    },
    {} as Record<TMediaType, TValue>
  )
}

export const normalizeVolume = (data?: TVolumeQuery): IPortfolioTrackingBrandVolume[] => {
  const workspace = data?.getWorkspace
  const nodes = collectBrandNodes(
    workspace?.workspaceBrandV2 as unknown as TBrandNode,
    workspace?.workspaceBrandCompetitorsPaginatedV2?.data as unknown as (TBrandNode | null)[]
  )
  return nodes.map(node => ({
    ...getBrandInfo(node),
    totalByMedia: buildByMedia(
      node.workspaceBrandTerritoriesV2,
      territory => territory.territoryPerformance?.metrics?.totalPosts ?? 0,
      0
    ),
  }))
}

export const normalizeVirality = (data?: TViralityQuery): IPortfolioTrackingBrandVirality[] => {
  const workspace = data?.getWorkspace
  const nodes = collectBrandNodes(
    workspace?.workspaceBrandV2 as unknown as TBrandNode,
    workspace?.workspaceBrandCompetitorsPaginatedV2?.data as unknown as (TBrandNode | null)[]
  )
  return nodes.map(node => ({
    ...getBrandInfo(node),
    viewsByMedia: buildByMedia(
      node.workspaceBrandTerritoriesV2,
      territory => territory.territoryPerformance?.metrics?.totalViews ?? 0,
      0
    ),
  }))
}

const isDateInput = (date: unknown): date is string | number | Date =>
  typeof date === 'string' || typeof date === 'number' || date instanceof Date

/**
 * The backend returns bucket dates as UTC midnight ("2026-07-01T00:00:00.000Z"). The x-axis label
 * generator formats dates in the browser's local timezone, so a UTC string would render as the
 * previous day/month for any timezone behind UTC. Stripping the UTC marker turns it into a naive
 * timestamp that's read as local, keeping the calendar date the backend actually meant.
 */
const toLocalDateString = (date: unknown): string =>
  dayjs.utc(isDateInput(date) ? date : null).format('YYYY-MM-DDTHH:mm:ss')

export const normalizeVelocity = (data?: TVelocityQuery): IPortfolioTrackingBrandVelocity[] => {
  const workspace = data?.getWorkspace
  const nodes = collectBrandNodes(
    workspace?.workspaceBrandV2 as unknown as TBrandNode,
    workspace?.workspaceBrandCompetitorsPaginatedV2?.data as unknown as (TBrandNode | null)[]
  )
  return nodes.map(node => ({
    ...getBrandInfo(node),
    sparklineByMedia: buildByMedia(
      node.workspaceBrandTerritoriesV2,
      (territory): IPortfolioTrackingSparklinePoint[] =>
        (territory.territoryPerformance?.videosPerPeriod?.sparkline ?? []).map(point => ({
          date: toLocalDateString(point?.date),
          videos: point?.videos ?? 0,
        })),
      [] as IPortfolioTrackingSparklinePoint[]
    ),
  }))
}

// `clusters` is a list in the schema ([CreatorClusterInterface!]!), so this stays a reduce rather than
// a [0] index — indexing would silently report 0 if the cardinality changed.
// V2 (shouldReturnTotalCreators): one CreatorClusterSubscriber, so the reduce sums one item — exact.
// V1 (no flag): 6 partial semantic clusters that can share creators, so this over-counts. That is
// pre-existing on the legacy path and is the very inflation the total mode fixes — the two paths are
// not comparable.
const readTierRows = (territory: TTerritoryNode, tier: TPortfolioTrackingTierId): number =>
  (territory.territoryCreators?.clusters ?? []).reduce((sum, cluster) => sum + (cluster?.[tier]?.rows ?? 0), 0)

const EMPTY_TIERS = (): Record<TPortfolioTrackingTierId, number> =>
  TIER_IDS.reduce(
    (acc, tier) => {
      acc[tier] = 0
      return acc
    },
    {} as Record<TPortfolioTrackingTierId, number>
  )

export const normalizeVariety = (data?: TVarietyQuery): IPortfolioTrackingBrandVariety[] => {
  const workspace = data?.getWorkspace
  const nodes = collectBrandNodes(
    workspace?.workspaceBrandV2 as unknown as TBrandNode,
    workspace?.workspaceBrandCompetitorsPaginatedV2?.data as unknown as (TBrandNode | null)[]
  )
  return nodes.map(node => ({
    ...getBrandInfo(node),
    tiersByMedia: buildByMedia(
      node.workspaceBrandTerritoriesV2,
      territory =>
        TIER_IDS.reduce(
          (acc, tier) => {
            acc[tier] = readTierRows(territory, tier)
            return acc
          },
          {} as Record<TPortfolioTrackingTierId, number>
        ),
      EMPTY_TIERS()
    ),
  }))
}

export const sumSelectedMedia = (byMedia: Record<TMediaType, number>, selected: TMediaType[]): number =>
  selected.reduce((sum, media) => sum + (byMedia[media] ?? 0), 0)
